import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";

export default function Contact({ api }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "General enquiry",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || form.message.length < 5) {
      toast.error("Please complete all fields (message ≥ 5 characters).");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${api}/contact`, form);
      toast.success("Message sent.", {
        description: "We'll reply within 2 working days.",
      });
      setForm({ name: "", email: "", subject: "General enquiry", message: "" });
    } catch (err) {
      toast.error("Couldn't send your message. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-32 bg-sand"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <p className="overline mb-6">In touch</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl text-ink leading-tight">
              Questions, partnerships, press —
              <br className="hidden md:block" />
              <em className="italic text-terracotta">write to us</em>.
            </h2>

            <div className="mt-10 space-y-6">
              <ContactRow
                icon={<Mail size={18} strokeWidth={1.5} />}
                label="Email"
                value="garajairamadas@gmail.com"
                testid="contact-email"
              />
              <ContactRow
                icon={<Phone size={18} strokeWidth={1.5} />}
                label="Phone"
                value={"+91-9113114592\n+91-9989740018"}
                testid="contact-phone"
              />
              <ContactRow
                icon={<MapPin size={18} strokeWidth={1.5} />}
                label="Head Office"
                value={"C1/3 Adityahomes, Sonari\nJamshedpur, Jharkhand 831011"}
                testid="contact-address"
              />
              <ContactRow
                icon={<MapPin size={18} strokeWidth={1.5} />}
                label="Branch Office"
                value={"2nd Floor, Civic House\nKanke Road — Ranchi, Jharkhand 834008"}
                testid="contact-address-branch"
              />
            </div>
          </div>

          <div className="md:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-cream border border-line p-7 md:p-10 space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Name">
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="contact-input-name"
                    className="cfield"
                  />
                </Field>
                <Field label="Email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    data-testid="contact-input-email"
                    className="cfield"
                  />
                </Field>
              </div>
              <Field label="Subject">
                <select
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="contact-select-subject"
                  className="cfield"
                >
                  <option>General enquiry</option>
                  <option>Partnerships</option>
                  <option>Press / media</option>
                  <option>Volunteer follow-up</option>
                  <option>Donation help</option>
                </select>
              </Field>
              <Field label="Message">
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  required
                  data-testid="contact-input-message"
                  className="cfield resize-none"
                />
              </Field>

              <div className="flex items-center justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-2 bg-forest hover:bg-terracotta text-cream hover:text-white px-7 py-3.5 transition-colors disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending
                    </>
                  ) : (
                    <>
                      Send message
                      <Send size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .cfield {
          width: 100%;
          background: #FFFFFF;
          border: 1px solid #E5E2D9;
          padding: 12px 14px;
          font-family: 'Work Sans', sans-serif;
          font-size: 15px;
          color: #1A1C1A;
          border-radius: 0;
        }
        .cfield:focus {
          outline: none;
          border-color: #C85A40;
          box-shadow: 0 0 0 3px rgba(200,90,64,0.12);
        }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="overline">{label}</span>
      {children}
    </label>
  );
}

function ContactRow({ icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-4" data-testid={testid}>
      <div className="w-10 h-10 bg-cream border border-line flex items-center justify-center flex-shrink-0 text-forest">
        {icon}
      </div>
      <div>
        <p className="overline mb-1">{label}</p>
        <p className="text-ink whitespace-pre-line leading-relaxed">{value}</p>
      </div>
    </div>
  );
}
